import { mediaReference, mediaReferenceKey } from '../utils/media.js'
import { normalizeYouTubeTrailer, normalizeVkTrailer } from '../utils/kinobdMedia.js'

export const LIBRARY_STORAGE_KEY = 'shinai:library:v1'
export const LIBRARY_VERSION = 1
export const ABANDONED_AFTER_MS = 14 * 24 * 60 * 60 * 1000
export const MAX_LIBRARY_ARTWORK_CANDIDATES = 12
export const MAX_LIBRARY_TRAILER_CANDIDATES = 6

export const LIBRARY_STATUS = Object.freeze({
    WATCH_LATER: 'watch-later',
    CONTINUE: 'continue',
    COMPLETED: 'completed',
})

export const LIBRARY_CATEGORY = Object.freeze({
    FAVORITES: 'favorites',
    WATCH_LATER: 'watch-later',
    CONTINUE: 'continue',
    COMPLETED: 'completed',
    ABANDONED: 'abandoned',
})

const VALID_KINDS = new Set(['anime', 'movie', 'series'])
const VALID_STATUSES = new Set(Object.values(LIBRARY_STATUS))
const VALID_CATEGORIES = new Set(Object.values(LIBRARY_CATEGORY))

function timestamp(value) {
    const parsed = Number(value)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

function finiteNumber(value) {
    if (value === null || value === undefined || value === '') return null
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

function text(value, fallback = '') {
    const normalized = String(value || '').trim()
    return normalized || fallback
}

function httpImage(value) {
    const candidate = typeof value === 'object' && value ? value.url : value
    if (!/^https?:\/\//i.test(String(candidate || ''))) return null
    try {
        return new URL(String(candidate)).toString()
    } catch (_) {
        return null
    }
}

function firstImage(values) {
    return (Array.isArray(values) ? values : []).map(httpImage).find(Boolean) || null
}

function artworkCandidate(value) {
    const url = httpImage(value)
    if (!url) return null
    const source = value && typeof value === 'object' ? value : {}
    return {
        url,
        width: finiteNumber(source.width),
        height: finiteNumber(source.height),
    }
}

function isPotentialHeroArtwork(candidate) {
    if (!candidate) return false
    const hasDimensions = candidate.width > 0 && candidate.height > 0
    if (!hasDimensions) return true
    return candidate.width >= 1280
        && candidate.height >= 720
        && candidate.width / candidate.height >= 1.35
}

function snapshotArtworkCandidates(media) {
    const poster = httpImage(media && media.poster)
    const values = [
        ...(Array.isArray(media && media.backdropCandidates) ? media.backdropCandidates : []),
        ...(Array.isArray(media && media.images && media.images.backdrops) ? media.images.backdrops : []),
        media && media.backdrop,
    ]
    const seen = new Set()
    const candidates = values.map(artworkCandidate).filter((candidate) => {
        if (
            !isPotentialHeroArtwork(candidate)
            || seen.has(candidate.url)
            || candidate.url === poster
        ) return false
        seen.add(candidate.url)
        return true
    })
    const bounded = candidates.slice(0, poster
        ? MAX_LIBRARY_ARTWORK_CANDIDATES - 1
        : MAX_LIBRARY_ARTWORK_CANDIDATES)
    if (poster) bounded.push({ url: poster, width: null, height: null })
    return bounded
}

function normalizedIds(ids = {}) {
    return {
        kinopoisk: finiteNumber(ids.kinopoisk),
        imdb: text(ids.imdb) || null,
        tmdb: finiteNumber(ids.tmdb),
        shikimori: text(ids.shikimori) || null,
    }
}

function snapshotScore(media) {
    const ratings = media && media.ratings || {}
    const candidates = [
        media && media.score,
        ratings.kinopoisk,
        ratings.imdb,
        ratings.shikimori,
    ].map(finiteNumber).filter((value) => value !== null)
    return candidates.length ? Math.max(...candidates) : null
}

export function normalizeLibraryMedia(media) {
    if (!media || typeof media !== 'object') return null

    const reference = mediaReference(media)
    if (!reference.id) return null
    const kind = VALID_KINDS.has(media.kind) ? media.kind : reference.kind
    const ids = normalizedIds(media.ids)
    const key = mediaReferenceKey({ ...reference, kind })
    const poster = httpImage(media.poster)
    const backdropCandidates = snapshotArtworkCandidates(media)
    const seenTrailers = new Set()
    const trailerCandidates = [
        media.trailer,
        ...(Array.isArray(media.trailerCandidates) ? media.trailerCandidates : []),
    ].map((candidate) => normalizeYouTubeTrailer(candidate) || normalizeVkTrailer(candidate))
        .filter((candidate) => {
            if (!candidate || seenTrailers.has(candidate.embedUrl)) return false
            seenTrailers.add(candidate.embedUrl)
            return true
        })
        .slice(0, MAX_LIBRARY_TRAILER_CANDIDATES)
    const trailer = trailerCandidates[0] || null

    return {
        key,
        provider: text(media.provider || reference.provider),
        id: text(media.id || reference.providerId || reference.id),
        kind,
        title: text(media.title, 'Без названия'),
        originalTitle: text(media.originalTitle),
        year: finiteNumber(media.year),
        yearEnd: finiteNumber(media.yearEnd),
        poster,
        backdrop: firstImage(backdropCandidates) || poster,
        backdropCandidates,
        trailer,
        trailerCandidates,
        subtitle: text(media.subtitle),
        description: text(media.description).slice(0, 1600),
        genres: (Array.isArray(media.genres) ? media.genres : [])
            .map((genre) => text(genre))
            .filter(Boolean)
            .slice(0, 4),
        score: snapshotScore(media),
        durationMinutes: finiteNumber(media.durationMinutes),
        episodes: finiteNumber(media.episodes),
        releaseType: text(media.releaseType),
        releaseStatus: text(media.releaseStatus || media.status),
        detailsHydrated: media.detailsHydrated === true,
        detailsHydratedAt: timestamp(media.detailsHydratedAt),
        trailerCheckedAt: timestamp(media.trailerCheckedAt),
        previewHydratedVersion: finiteNumber(media.previewHydratedVersion),
        isEpisodic: typeof media.isEpisodic === 'boolean'
            ? media.isEpisodic
            : (kind === 'series' ? true : (kind === 'movie' ? false : null)),
        ids,
    }
}

function preferIncoming(current, incoming) {
    return incoming !== null && incoming !== undefined && incoming !== '' ? incoming : current
}

export function mergeLibraryMedia(current, incoming) {
    if (!current) return incoming
    if (!incoming) return current

    const merged = { ...current, ...incoming }
    for (const field of [
        'provider', 'id', 'originalTitle', 'poster', 'backdrop', 'subtitle', 'description',
        'releaseType', 'releaseStatus',
    ]) {
        merged[field] = preferIncoming(current[field], incoming[field])
    }
    merged.title = incoming.title && incoming.title !== 'Без названия'
        ? incoming.title
        : current.title
    for (const field of [
        'year', 'yearEnd', 'score', 'durationMinutes', 'episodes', 'isEpisodic',
        'previewHydratedVersion',
    ]) {
        merged[field] = incoming[field] !== null && incoming[field] !== undefined
            ? incoming[field]
            : current[field]
    }
    merged.genres = incoming.genres && incoming.genres.length ? incoming.genres : current.genres
    merged.backdropCandidates = incoming.backdropCandidates && incoming.backdropCandidates.length
        ? incoming.backdropCandidates
        : current.backdropCandidates
    merged.trailer = incoming.trailer || current.trailer || null
    merged.trailerCandidates = incoming.trailerCandidates && incoming.trailerCandidates.length
        ? incoming.trailerCandidates
        : current.trailerCandidates
    merged.detailsHydrated = Boolean(current.detailsHydrated || incoming.detailsHydrated)
    merged.detailsHydratedAt = incoming.detailsHydratedAt || current.detailsHydratedAt || null
    merged.trailerCheckedAt = incoming.trailerCheckedAt || current.trailerCheckedAt || null
    merged.ids = Object.fromEntries(Object.keys(current.ids || incoming.ids || {}).map((name) => [
        name,
        preferIncoming(current.ids && current.ids[name], incoming.ids && incoming.ids[name]),
    ]))
    return merged
}

function normalizeProgress(progress = {}) {
    let fraction = finiteNumber(progress.fraction)
    if (fraction === null) {
        const percent = finiteNumber(progress.percent)
        if (percent !== null) fraction = percent / 100
    }
    const currentTime = finiteNumber(progress.currentTime)
    const duration = finiteNumber(progress.duration)
    if (fraction === null && currentTime !== null && duration > 0) fraction = currentTime / duration
    if (fraction !== null) fraction = Math.min(1, Math.max(0, fraction))

    return {
        fraction,
        currentTime,
        duration,
        season: finiteNumber(progress.season),
        episode: finiteNumber(progress.episode),
        ended: Boolean(progress.ended || progress.completed),
    }
}

function normalizeRecord(record) {
    if (!record || typeof record !== 'object') return null
    const media = normalizeLibraryMedia(record.media)
    if (!media) return null
    const status = VALID_STATUSES.has(record.status) ? record.status : null

    return {
        key: media.key,
        media,
        favoriteAt: timestamp(record.favoriteAt),
        status,
        statusChangedAt: timestamp(record.statusChangedAt),
        watchLaterAt: timestamp(record.watchLaterAt),
        continueAt: timestamp(record.continueAt),
        startedAt: timestamp(record.startedAt),
        lastWatchedAt: timestamp(record.lastWatchedAt),
        completedAt: timestamp(record.completedAt),
        progress: normalizeProgress(record.progress),
    }
}

function recordLifecycleTime(record) {
    return Math.max(
        record.statusChangedAt || 0,
        record.watchLaterAt || 0,
        record.continueAt || 0,
        record.lastWatchedAt || 0,
        record.completedAt || 0
    )
}

function mergeRecords(current, incoming) {
    if (!current) return incoming
    const newer = recordLifecycleTime(incoming) >= recordLifecycleTime(current) ? incoming : current
    const older = newer === incoming ? current : incoming
    return {
        ...newer,
        key: incoming.key,
        media: mergeLibraryMedia(older.media, newer.media),
        favoriteAt: Math.max(current.favoriteAt || 0, incoming.favoriteAt || 0) || null,
    }
}

export function normalizeLibraryState(value) {
    const sourceItems = value && typeof value.items === 'object' && value.items
        ? value.items
        : {}
    const items = {}
    Object.values(sourceItems).forEach((candidate) => {
        const record = normalizeRecord(candidate)
        if (!record || (!record.favoriteAt && !record.status)) return
        items[record.key] = mergeRecords(items[record.key], record)
    })
    return { version: LIBRARY_VERSION, items }
}

export function emptyLibraryState() {
    return { version: LIBRARY_VERSION, items: {} }
}

export function readLibraryState(storage = typeof localStorage === 'undefined' ? null : localStorage) {
    if (!storage || typeof storage.getItem !== 'function') return emptyLibraryState()
    try {
        const value = storage.getItem(LIBRARY_STORAGE_KEY)
        return value ? normalizeLibraryState(JSON.parse(value)) : emptyLibraryState()
    } catch (_) {
        return emptyLibraryState()
    }
}

export function writeLibraryState(state, storage = typeof localStorage === 'undefined' ? null : localStorage) {
    if (!storage || typeof storage.setItem !== 'function') return false
    try {
        storage.setItem(LIBRARY_STORAGE_KEY, JSON.stringify(normalizeLibraryState(state)))
        return true
    } catch (_) {
        return false
    }
}

function recordCategory(record, now = Date.now()) {
    if (!record || record.status !== LIBRARY_STATUS.CONTINUE) return null
    const lastVisit = record.lastWatchedAt || record.startedAt || record.statusChangedAt
    return lastVisit && now - lastVisit > ABANDONED_AFTER_MS
        ? LIBRARY_CATEGORY.ABANDONED
        : LIBRARY_CATEGORY.CONTINUE
}

export function libraryRecordInCategory(record, category, now = Date.now()) {
    if (!record || !VALID_CATEGORIES.has(category)) return false
    if (category === LIBRARY_CATEGORY.FAVORITES) return Boolean(record.favoriteAt)
    if (category === LIBRARY_CATEGORY.WATCH_LATER) return record.status === LIBRARY_STATUS.WATCH_LATER
    if (category === LIBRARY_CATEGORY.COMPLETED) return record.status === LIBRARY_STATUS.COMPLETED
    return recordCategory(record, now) === category
}

function categoryTimestamp(record, category) {
    if (category === LIBRARY_CATEGORY.FAVORITES) return record.favoriteAt
    if (category === LIBRARY_CATEGORY.WATCH_LATER) return record.watchLaterAt || record.statusChangedAt
    if (category === LIBRARY_CATEGORY.COMPLETED) return record.completedAt || record.statusChangedAt
    if (category === LIBRARY_CATEGORY.CONTINUE) {
        return record.continueAt || record.startedAt || record.statusChangedAt
    }
    return record.lastWatchedAt || record.startedAt || record.statusChangedAt
}

export function libraryItemsForCategory(state, category, now = Date.now()) {
    const normalized = normalizeLibraryState(state)
    return Object.values(normalized.items)
        .filter((record) => libraryRecordInCategory(record, category, now))
        .sort((left, right) => {
            return (categoryTimestamp(right, category) || 0) - (categoryTimestamp(left, category) || 0)
                || left.media.title.localeCompare(right.media.title, 'ru')
        })
}

export function isPlaybackComplete(progress, media) {
    const normalized = normalizeProgress(progress)
    const episodes = finiteNumber(media && media.episodes)
    const singleTitle = media && media.kind === 'movie'
        || (media && media.kind === 'anime' && (
            media.isEpisodic === false
            || (media.isEpisodic !== true && (!episodes || episodes <= 1))
        ))

    if (singleTitle) return normalized.ended || normalized.fraction >= 0.95
    if (/ongoing|anons|анонс|в работе/iu.test(String(media && media.releaseStatus || ''))) return false
    if (!episodes || !normalized.episode || normalized.episode < episodes) return false
    return normalized.ended || normalized.fraction >= 0.9
}

function cloneRecord(record) {
    return record ? {
        ...record,
        media: {
            ...record.media,
            ids: { ...record.media.ids },
            genres: [...record.media.genres],
            backdropCandidates: (record.media.backdropCandidates || []).map((candidate) => ({ ...candidate })),
            trailer: record.media.trailer ? { ...record.media.trailer } : null,
            trailerCandidates: (record.media.trailerCandidates || []).map((candidate) => ({ ...candidate })),
        },
        progress: { ...record.progress },
    } : null
}

function createRecord(media) {
    return {
        key: media.key,
        media,
        favoriteAt: null,
        status: null,
        statusChangedAt: null,
        watchLaterAt: null,
        continueAt: null,
        startedAt: null,
        lastWatchedAt: null,
        completedAt: null,
        progress: normalizeProgress(),
    }
}

function cleanRecord(items, record) {
    if (!record.favoriteAt && !record.status) {
        delete items[record.key]
    } else {
        items[record.key] = record
    }
}

export function reduceLibrary(state, action = {}, now = Date.now()) {
    const normalized = normalizeLibraryState(state)
    const items = { ...normalized.items }
    const media = action.media ? normalizeLibraryMedia(action.media) : null
    const key = media && media.key || text(action.key)
    if (!key) return normalized

    let record = cloneRecord(items[key])
    if (!record && media) {
        const matchingEntry = Object.entries(items).find(([, candidate]) => {
            return candidate.media.kind === media.kind
                && candidate.media.provider === media.provider
                && candidate.media.id === media.id
        })
        if (matchingEntry) {
            record = cloneRecord(matchingEntry[1])
            delete items[matchingEntry[0]]
            record.key = key
        }
    }
    if (!record && action.type === 'enrich-media') return normalized
    if (!record && media) record = createRecord(media)
    if (!record) return normalized
    if (media) record.media = mergeLibraryMedia(record.media, media)

    const eventTime = timestamp(action.now) || timestamp(now) || Date.now()
    switch (action.type) {
    case 'enrich-media':
        break
    case 'toggle-favorite':
        record.favoriteAt = record.favoriteAt ? null : eventTime
        break
    case 'set-favorite':
        record.favoriteAt = action.value ? (record.favoriteAt || eventTime) : null
        break
    case 'toggle-watch-later':
        if (record.status === LIBRARY_STATUS.WATCH_LATER) {
            record.status = null
            record.statusChangedAt = null
            record.watchLaterAt = null
        } else {
            const restartCycle = record.status === LIBRARY_STATUS.COMPLETED
            record.status = LIBRARY_STATUS.WATCH_LATER
            record.statusChangedAt = eventTime
            record.watchLaterAt = eventTime
            record.continueAt = null
            record.completedAt = null
            if (restartCycle) {
                record.progress = normalizeProgress()
                record.startedAt = null
                record.lastWatchedAt = null
            }
        }
        break
    case 'set-watch-later':
        if (action.value) {
            const restartCycle = record.status === LIBRARY_STATUS.COMPLETED
            record.status = LIBRARY_STATUS.WATCH_LATER
            record.statusChangedAt = eventTime
            record.watchLaterAt = eventTime
            record.continueAt = null
            record.completedAt = null
            if (restartCycle) {
                record.progress = normalizeProgress()
                record.startedAt = null
                record.lastWatchedAt = null
            }
        } else if (record.status === LIBRARY_STATUS.WATCH_LATER) {
            record.status = null
            record.statusChangedAt = null
            record.watchLaterAt = null
        }
        break
    case 'start-playback': {
        const restart = record.status === LIBRARY_STATUS.COMPLETED
        record.status = LIBRARY_STATUS.CONTINUE
        record.statusChangedAt = eventTime
        record.continueAt = eventTime
        record.startedAt = restart ? eventTime : (record.startedAt || eventTime)
        record.lastWatchedAt = eventTime
        record.watchLaterAt = null
        record.completedAt = null
        if (restart) record.progress = normalizeProgress()
        break
    }
    case 'update-progress': {
        if (record.status === LIBRARY_STATUS.COMPLETED) break
        const progress = normalizeProgress(action.progress)
        const previousProgress = record.progress
        record.status = LIBRARY_STATUS.CONTINUE
        record.statusChangedAt = record.statusChangedAt || eventTime
        record.continueAt = record.continueAt || eventTime
        record.startedAt = record.startedAt || eventTime
        record.lastWatchedAt = eventTime
        record.watchLaterAt = null
        const episodeChanged = progress.episode !== null
            && progress.episode !== previousProgress.episode
        const seasonChanged = progress.season !== null
            && progress.season !== previousProgress.season
        const nextProgress = { ...previousProgress }
        if (episodeChanged || seasonChanged) {
            nextProgress.fraction = null
            nextProgress.currentTime = null
            nextProgress.duration = null
            nextProgress.ended = false
            if (seasonChanged) nextProgress.episode = null
        }
        for (const field of ['fraction', 'currentTime', 'duration', 'season', 'episode']) {
            if (progress[field] !== null) nextProgress[field] = progress[field]
        }
        const hasEndedValue = action.progress && (
            Object.prototype.hasOwnProperty.call(action.progress, 'ended')
            || Object.prototype.hasOwnProperty.call(action.progress, 'completed')
        )
        if (hasEndedValue) nextProgress.ended = progress.ended
        record.progress = nextProgress
        if (isPlaybackComplete(record.progress, record.media)) {
            record.status = LIBRARY_STATUS.COMPLETED
            record.statusChangedAt = eventTime
            record.completedAt = eventTime
        }
        break
    }
    case 'complete':
        record.status = LIBRARY_STATUS.COMPLETED
        record.statusChangedAt = eventTime
        record.lastWatchedAt = record.lastWatchedAt || eventTime
        record.completedAt = eventTime
        record.watchLaterAt = null
        record.progress = { ...record.progress, fraction: 1, ended: true }
        break
    case 'remove-category':
        if (action.category === LIBRARY_CATEGORY.FAVORITES) {
            record.favoriteAt = null
        } else if (libraryRecordInCategory(record, action.category, eventTime)) {
            record.status = null
            record.statusChangedAt = null
            record.watchLaterAt = null
            record.continueAt = null
            record.completedAt = null
            record.progress = normalizeProgress()
            record.startedAt = null
            record.lastWatchedAt = null
        }
        break
    default:
        return normalized
    }

    cleanRecord(items, record)
    return { version: LIBRARY_VERSION, items }
}
